import type { Room } from "@catmail/shared";

import { Avatar, Badge } from "@/components";
import { cn } from "@/lib/cn";
import { FOCUS, INTERACTIVE, PRESS_FLAT } from "@/lib/interaction";
import { formatRoomTimestamp } from "@/lib/datetime";

import {
  avatarToneOf,
  roomInitialsOf,
  roomLeadOf,
  roomTitleOf,
} from "../../lib/roomView";

type RoomListItemProps = {
  room: Room;
  selected: boolean;
  onSelect: (roomId: string) => void;
  /** 아바타를 누르면 상대 프로필이 열린다 (시안 02-1) */
  onOpenProfile: (roomId: string) => void;
};

export function RoomListItem({
  room,
  selected,
  onSelect,
  onOpenProfile,
}: RoomListItemProps) {
  const lead = roomLeadOf(room);
  const title = roomTitleOf(room);
  const memberCount = room.participants.length;
  /**
   * 말 걸기 전에 연 방. 아직 한 통도 오가지 않았다.
   *
   * 미리보기는 메일이 오갈 때만 채워지므로(`mail/ingest.ts` · `routes/messages.ts`)
   * 비어 있다는 것이 곧 "아직 아무것도 없다"는 뜻이다.
   */
  const empty = room.preview === null;

  return (
    // 행 전체와 아바타는 각각 다른 곳으로 간다. 버튼을 중첩할 수 없으므로
    // 아바타를 겹쳐 띄우고 행에는 그만큼 왼쪽 여백을 준다.
    <div className="relative">
      <button
        type="button"
        aria-current={selected ? "true" : undefined}
        onClick={() => onSelect(room.id)}
        className={cn(
          `flex w-full items-center gap-3.5 py-3.75 pr-5.5 pl-20 text-left focus-visible:outline-offset-[-2px] ${PRESS_FLAT} ${FOCUS}`,
          selected
            ? "bg-selected border-ink border-l-3 pl-[77px]"
            : "hover:bg-paper/60 active:bg-chip",
        )}
      >
        <span className="flex min-w-0 flex-1 flex-col gap-1.25">
          <span className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                "line-clamp-1-ko text-[15px] font-semibold",
                room.isQuiet ? "text-quiet-text" : "text-ink",
              )}
            >
              {title}
              {room.kind === "group" ? (
                <span className="text-muted-2 ml-1.5 font-medium">
                  {memberCount}
                </span>
              ) : null}
            </span>
            <span
              className={cn(
                "shrink-0 text-xs",
                room.isQuiet ? "text-muted-3" : "text-muted-2",
              )}
            >
              {/*
                말 걸기 전에 연 방에는 시각을 적지 않는다. `lastMessageAt`에는
                방을 만든 시각이 들어 있는데(목록 정렬과 커서가 그 값을 쓴다),
                그걸 그대로 보여주면 그때 메일이 온 것처럼 읽힌다.
              */}
              {empty ? "" : formatRoomTimestamp(room.lastMessageAt)}
            </span>
          </span>

          <span className="flex items-center gap-2">
            {/*
              ⚠️ **빈 문자열을 넣으면 줄이 무너진다.**

              `line-clamp-1-ko`는 내용이 없으면 줄 상자를 만들지 않는다. 그래서
              미리보기가 없는 방은 이 줄의 높이가 0이 되고, 그 한 줄만 짧아져서
              옆 아바타와 어긋난다 — 목록이 깨진 것처럼 보인다. 실제로 그렇게
              내보냈다. 아무것도 없다는 것도 **글자로** 말해야 자리를 차지한다.
            */}
            <span
              className={cn(
                "line-clamp-1-ko flex-1 text-sm",
                empty ? "text-muted-3" : room.isQuiet ? "text-muted" : "text-body",
              )}
            >
              {empty ? (
                "아직 주고받은 메일이 없어요"
              ) : (
                <>
                  {room.preview?.senderName ? `${room.preview.senderName}: ` : ""}
                  {room.preview?.text ?? ""}
                </>
              )}
            </span>
            <Badge
              count={room.unreadCount}
              tone={room.isQuiet ? "muted" : "alert"}
            />
          </span>
        </span>
      </button>

      <button
        type="button"
        aria-label={`${title} 프로필 열기`}
        onClick={() => onOpenProfile(room.id)}
        // 행마다 하나씩 있는 큰 표적인데 눌린 티도 초점 테두리도 없었다.
        className={`absolute top-3.75 left-5.5 rounded-avatar ${INTERACTIVE}`}
      >
        <Avatar
          size="xl"
          initials={roomInitialsOf(room)}
          colorIndex={lead?.colorIndex ?? 0}
          imageUrl={lead?.avatarUrl}
          dotTone={
            room.kind === "group" || !lead ? null : avatarToneOf(lead)
          }
          label={title}
        />
      </button>
    </div>
  );
}
